/**
 * Top-of-Book Detection
 *
 * Determines if an order is at the best price level in the orderbook.
 * Used by CombinedOrdersPositionsPanel to show a top-of-book indicator.
 *
 * @ai-context
 *   Role: Checks if an order sits at the best bid/ask. Display indicator only.
 *   Pattern: Pure function. Compares order price to book best prices.
 *   Purity: No side effects, no API calls.
 *
 * @ai-invariant All prices in cents. Ask prices are derived (100 - bid)
 *   by the caller who populates TickerOrderbook — this function just compares.
 *
 * @ai-downstream
 *   - CombinedOrdersPositionsPanel → isAtTopOfBook
 *   - topOfBook.test.ts (15 cases)
 */

/** Orderbook best bid/ask for a single market ticker */
export interface TickerOrderbook {
  /** Best YES bid price in cents */
  yesBestBid: number | null;
  /** Best YES ask price in cents (derived: 100 - best NO bid) */
  yesBestAsk: number | null;
  /** Best NO bid price in cents */
  noBestBid: number | null;
  /** Best NO ask price in cents (derived: 100 - best YES bid) */
  noBestAsk: number | null;
}

/**
 * Determine if an order is at the top of book (best price).
 * - BUY orders: top if order price >= best bid
 * - SELL orders: top if order price <= best ask
 *
 * Returns null if orderbook data is unavailable.
 */
export function isAtTopOfBook(args: {
  action: 'buy' | 'sell';
  side: 'yes' | 'no';
  orderPriceCents: number;
  orderbook: TickerOrderbook | undefined;
}): boolean | null {
  const { action, side, orderPriceCents, orderbook } = args;
  if (!orderbook) return null;

  if (action === 'buy') {
    // Buying: compare to best bid for this side
    const bestBid = side === 'yes' ? orderbook.yesBestBid : orderbook.noBestBid;
    if (bestBid === null) return null;
    return orderPriceCents >= bestBid;
  }

  // Selling: compare to best ask for this side
  const bestAsk = side === 'yes' ? orderbook.yesBestAsk : orderbook.noBestAsk;
  if (bestAsk === null) return null;
  return orderPriceCents <= bestAsk;
}
